import {
  ApolloLink,
  createSignalIfSupported,
  fallbackHttpConfig,
  selectHttpOptionsAndBody,
  selectURI,
  serializeFetchParameter
} from '@apollo/client/core';

import { Observable, map } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';

export const extractFiles = (tree, treePath = '') => {
  const files = [];
  const recurse = (node, nodePath) => {
    // Iterate enumerable properties of the node.
    Object.keys(node).forEach((key) => {
      // Skip non-object.
      if (!isObject(node[key])) {
        return;
      }
      const path = `${nodePath}${key}`;
      if (
        // Node is a File.
        (typeof File !== 'undefined' && node[key] instanceof File) ||
        // Node is a Blob.
        (typeof Blob !== 'undefined' && node[key] instanceof Blob)
      ) {
        // Extract the file and it's object tree path.
        files.push({ path, file: node[key] });
        // Delete the file. Array items must be deleted without reindexing to
        // allow repopulation in a reverse operation.
        node[key] = null;
        // No further checks or recursion.
        return;
      }

      if (typeof FileList !== 'undefined' && node[key] instanceof FileList) {
        // Convert read-only FileList to an array for manipulation.
        node[key] = Array.prototype.slice.call(node[key]);
      }
      // Recurse into child node.
      recurse(node[key], `${path}.`);
    });
  };

  if (isObject(tree)) {
    // Recurse object tree.
    recurse(
      tree,
      // If a tree path was provided, append a dot.
      treePath === '' ? treePath : `${treePath}.`
    );
  }
  return files;
};

export const createScApolloLink = ({
  fetchUri,
  httpClient,
  authService,
  cookies
}: {
  fetchUri: string;
  httpClient: HttpClient;
  authService?: any;
  cookies?: any;
}) => {
  return new ApolloLink((operation): any => {
    const { options, body } = selectHttpOptionsAndBody(operation, fallbackHttpConfig);
    const uri = selectURI(operation, fetchUri);
    const queryParam = operation.query.definitions.map((def) => def['name'].value).join('+');
    const files = extractFiles(body);
    const payload = serializeFetchParameter(body, 'Payload');
    const contextHeaders = operation.getContext().headers;
    if (contextHeaders) {
      options.headers = {
        ...options.headers,
        ...contextHeaders
      };
    }

    if (authService?.getAuthHeaders().get('Authorization')) {
      options.headers.Authorization = authService.getAuthHeaders().get('Authorization');
    }

    const csrfToken = cookies?.get(environment.csrfCookieName);
    if (csrfToken) {
      options.headers['X-CSRFToken'] = csrfToken;
    }

    options.headers['content-type'] = 'application/json';
    if (files.length) {
      // Automatically set by fetch when the body is a FormData instance.
      delete options.headers['content-type'];
      options.body = new FormData();
      options.body.append('operations', payload);
      options.body.append(
        'map',
        JSON.stringify(
          files.reduce((acc, { path }, index) => {
            acc[`${index}`] = [path];
            return acc;
          }, {})
        )
      );
      files.forEach(({ file }, index) => options.body.append(index, file, file.name));
    } else {
      options.body = payload;
    }

    return new Observable((observer) => {
      // Allow aborting fetch, if supported.
      const { controller, signal } = createSignalIfSupported();
      if (controller) {
        options.signal = signal;
      }
      httpClient
        .post(`${uri}?queryName=${queryParam}`, options.body, options)
        .pipe(
          map((response) => {
            // Forward the response on the context.
            operation.setContext({ response });
            return response;
          })
        )
        .subscribe({
          next: (result) => {
            observer.next(result);
            observer.complete();
          },
          error: (error) => {
            if (error.name === 'AbortError') {
              return;
            }
            if (error.result && error.result.errors && error.result.data) {
              observer.next(error.result);
            }
            observer.error(error);
          }
        });

      // Cleanup function.
      return () => {
        // Abort fetch.
        if (controller instanceof AbortController) {
          controller.abort();
        }
      };
    });
  });
};

function isObject(x) {
  return x != null && typeof x === 'object';
}
